// frontend/src/lib/api.js
// ✅ GOOGLE OAUTH VERSION - Clean, simple, no Pi SDK

import { getIdentityHeaders, getIdToken } from "./identityGoogle.js";

// ---------- Environment Detection ----------

const isLocal =
  typeof window !== "undefined" &&
  /^(localhost|127\.0\.0\.1)(:\d+)?$/.test(window.location.hostname);

// ---------- Base URL Selection ----------

// ✅ UPDATED: New Google Cloud Run URL
const CLOUD_RUN_URL = "https://artibytes-google-api-191042099955.europe-west4.run.app";

// ✅ FIX: React env vars are in process.env at build time
const ENV_API = process.env.REACT_APP_API_BASE;

// Runtime override — DEVELOPMENT ONLY.
// ✅ SECURITY: previously this was honored in production and took top
// priority, meaning any script that set window.__ARTIBYTES_API_BASE__
// before the bundle loaded (compromised third-party script, or a
// "paste this in your console" social-engineering attack) could silently
// redirect every API call — Bearer token included — to a hostile server.
// Now it only works on localhost.
const RUNTIME_BASE =
  isLocal && typeof window !== "undefined" && window.__ARTIBYTES_API_BASE__
    ? String(window.__ARTIBYTES_API_BASE__)
    : "";

// Determine BASE URL - ENV_API takes priority!
let BASE = "";
if (RUNTIME_BASE) {
  BASE = RUNTIME_BASE;
} else if (ENV_API) {
  BASE = ENV_API;  // ✅ Use env var if set
} else if (isLocal) {
  BASE = "https://artibytes-google-api-191042099955.europe-west4.run.app"; // Only if no ENV_API
} else {
  BASE = CLOUD_RUN_URL; // Production default
}

BASE = BASE.replace(/\/+$/, "");

// ✅ DEBUG: Log configuration on startup
console.log('[API] Configuration:', {
  ENV_API,
  isLocal,
  BASE,
  CLOUD_RUN_URL
});

const withBase = (p) => {
  const path = p.startsWith("/") ? p : `/${p}`;
  return BASE ? `${BASE}${path}` : path;
};

// Use 'include' for credentials (works with both CORS and same-origin)
const defaultCreds = "include";

// ---------- Headers ----------

async function getHeaders(customHeaders = {}) {
  return {
    "Content-Type": "application/json",
    "Accept": "application/json",
    ...getIdentityHeaders(),
    ...customHeaders,
  };
}

// ---------- Response Helpers ----------

async function toJson(res) {
  const ct = res.headers.get("content-type") || "";
  return ct.includes("application/json") ? res.json() : res.text();
}

async function handle(res) {
  const data = await toJson(res);
  if (!res.ok) {
    const msg = typeof data === "string" ? data : data?.error || res.statusText || "Request failed";
    const err = new Error(`HTTP ${res.status}: ${msg}`);
    err.status = res.status;
    console.error("[API]", err.message);
    throw err;
  }
  return data;
}

// ---------- Public API Methods ----------

/**
 * GET request
 */
export async function apiGet(path, init = {}) {
  const headers = await getHeaders(init.headers);
  
  const { method, ...restInit } = init; // Remove method to prevent override

  const res = await fetch(withBase(path), {
    ...restInit,
    method: "GET",
    credentials: restInit.credentials || defaultCreds,
    headers,
  });

  return handle(res);
}

/**
 * POST request
 */
export async function apiPost(path, body, init = {}) {
  const headers = await getHeaders(init.headers);

  const res = await fetch(withBase(path), {
    method: "POST",
    credentials: init.credentials || defaultCreds,
    headers,
    body: JSON.stringify(body ?? {}),
    ...init,
  });

  return handle(res);
}

/**
 * PATCH request
 */
export async function apiPatch(path, body, init = {}) {
  const headers = await getHeaders(init.headers);

  const res = await fetch(withBase(path), {
    method: "PATCH",
    credentials: init.credentials || defaultCreds,
    headers,
    body: JSON.stringify(body ?? {}),
    ...init,
  });

  return handle(res);
}

/**
 * DELETE request
 */
export async function apiDelete(path, init = {}) {
  const headers = await getHeaders(init.headers);

  const res = await fetch(withBase(path), {
    method: "DELETE",
    credentials: init.credentials || defaultCreds,
    headers,
    ...init,
  });

  return handle(res);
}

// ---------- Feedback API Helpers ----------

export const feedback = {
  // Submit a style report
  async reportStyle(styleId, { reason, generationId }) {
    return apiPost(`/api/feedback/styles/${styleId}/report`, { reason, generationId });
  },

  // Like a style
  async likeStyle(styleId, { generationId }) {
    return apiPost(`/api/feedback/styles/${styleId}/like`, { generationId });
  },

  // Get my style reports (as creator)
  async getMyStyleReports(status = 'all') {
    return apiGet(`/api/feedback/my-styles/reports?status=${status}`);
  },

  // Resolve a report (as creator)
  async resolveReport(reportId, action) {
    return apiPatch(`/api/feedback/my-styles/reports/${reportId}/resolve`, { action });
  },

  // Get style stats
  async getStyleStats(styleId) {
    return apiGet(`/api/feedback/styles/${styleId}/stats`);
  },

  // Get bulk style stats
  async getBulkStyleStats() {
    return apiGet('/api/feedback/styles/bulk-stats');
  },
};

// ---------- Exports ----------

export const API_BASE = BASE;

export const ENV_INFO = {
  isLocal,
  API_BASE: BASE,
  ENV_API,
  RUNTIME_BASE,
  credentials: defaultCreds,
};

// ---------- Debug Helper ----------

if (typeof window !== "undefined") {
  window.debugArtiBytes = () => {
    console.group("🔍 ArtiBytes API Configuration");
    console.log("Is Localhost:", ENV_INFO.isLocal);
    console.log("API Base URL:", ENV_INFO.API_BASE);
    console.log("Env Override:", ENV_INFO.ENV_API || "(none)");
    console.log("Runtime Override:", ENV_INFO.RUNTIME_BASE || "(none)");
    console.log("Credentials:", ENV_INFO.credentials);

    // Show auth status
    try {
      const token = getIdToken();
      if (token) {
        // Decode JWT to show info (basic decode, not verification)
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const payload = JSON.parse(atob(base64));
        
        console.log("Authenticated:", true);
        console.log("Email:", payload.email);
        console.log("Name:", payload.name);
        console.log("Token expires:", new Date(payload.exp * 1000).toLocaleString());
      } else {
        console.log("Authenticated:", false);
      }
    } catch (e) {
      console.log("Auth status: error reading token");
    }

    console.groupEnd();
  };
}

/* ==================== Utility Functions ==================== */

export async function readJSON(res) {
  const text = await res.text();
  try {
    return JSON.parse(text);
  } catch {
    if (text.startsWith("<")) throw new Error("Backend response was HTML. Check your API configuration.");
    throw new Error("Invalid JSON from server.");
  }
}

export function normalizeListResponse(json) {
  if (Array.isArray(json)) return { items: json, nextCursor: null };
  return {
    items: Array.isArray(json?.items) ? json.items : [],
    nextCursor: json?.nextCursor || null,
  };
}

export function getImageUrl(item) {
  const url = item?.imageUrl || item?.cdnUrl || item?.resultUrl || null;
  if (!url) return null;
  if (url.startsWith("/api/") && BASE) return `${BASE}${url}`;
  return url;
}

export function getThumbUrl(item) {
  return item?.thumbUrl || getImageUrl(item);
}

export function fromNow(iso) {
  try {
    const d = new Date(iso);
    const s = Math.max(1, Math.floor((Date.now() - d.getTime()) / 1000));
    const units = [
      ["yr", 31536000],
      ["mo", 2592000],
      ["d", 86400],
      ["h", 3600],
      ["m", 60],
      ["s", 1],
    ];
    for (const [u, v] of units) if (s >= v) return `${Math.floor(s / v)}${u} ago`;
  } catch {}
  return "";
}

export function shorten(id = "") {
  if (!id) return "";
  return id.length <= 12 ? id : `${id.slice(0, 6)}…${id.slice(-6)}`;
}

// ✅ UPDATED: Changed from Pi (π) to USD ($)
export function fmtUSD(x) {
  if (!Number.isFinite(x)) return "—";
  return `$${x.toFixed(2)}`;
}

// Keep old function name for backward compatibility, but use USD
export function fmtPi(x) {
  return fmtUSD(x);
}

export function toast(text) {
  const n = document.createElement("div");
  n.textContent = text;
  Object.assign(n.style, {
    position: "fixed",
    left: "50%",
    bottom: "28px",
    transform: "translateX(-50%)",
    background: "rgba(20,20,30,.92)",
    color: "#fff",
    padding: "10px 14px",
    borderRadius: "10px",
    fontSize: "13px",
    zIndex: 9999,
    boxShadow: "0 8px 18px rgba(0,0,0,.25)",
    pointerEvents: "none",
  });
  document.body.appendChild(n);
  setTimeout(() => n.remove(), 1600);
}